import {Preferences} from '@capacitor/preferences';
import {PreferenceKey} from '@/definitions/PreferenceKey';
import {asIso} from '@/services/api/normalizeFeed';

let cachedAuthUserId: number | null = null;

async function authUserId(): Promise<number | null> {
    if (cachedAuthUserId != null) return cachedAuthUserId;
    const raw = (await Preferences.get({key: PreferenceKey.UserId})).value;
    if (raw) cachedAuthUserId = Number(raw);
    return cachedAuthUserId;
}

function normalizeParticipant(raw: any, me: number | null): any {
    if (!raw || typeof raw !== 'object') return raw;
    const user = raw.user || raw.profile || raw;
    const id = Number(user.id ?? raw.userId ?? raw.user_id ?? 0);
    const name = user.name || user.userName || user.user_name || user.slug || 'Unknown';
    return {
        ...user,
        id,
        name,
        slug: user.slug || user.userName || String(id),
        avatar: user.avatar
            || user.profilePhoto
            || user.profile_photo
            || user.avatarUrl
            || user.profilePhotoPath
            || null,
        role: raw.role || user.role || (raw.isAdmin || raw.is_admin ? 'ADMIN' : 'MEMBER'),
        is_auth_user: Boolean(
            raw.is_auth_user
            ?? raw.isAuthUser
            ?? raw.isMe
            ?? (me != null && id === me),
        ),
        subscribed: Boolean(user.isPremium ?? user.is_premium ?? user.subscribed),
        id_verified: Boolean(
            user.isVerified
            ?? user.id_verified
            ?? user.idVerified
            ?? (String(user.verificationStatus || '').toUpperCase() === 'APPROVED'),
        ),
        account_type: user.account_type || user.accountType,
    };
}

function normalizeMessage(raw: any, me: number | null): any {
    if (!raw || typeof raw !== 'object') return raw;

    const postedByRaw = raw.posted_by || raw.postedBy || raw.user || raw.sender || raw.author;
    const postedBy = postedByRaw
        ? normalizeParticipant(postedByRaw, me)
        : {
            id: Number(raw.userId ?? raw.user_id ?? 0),
            name: 'Unknown',
            avatar: null,
            is_auth_user: me != null && Number(raw.userId ?? raw.user_id) === me,
        };

    const text = raw.original_message
        ?? raw.originalMessage
        ?? raw.message
        ?? raw.content
        ?? raw.text
        ?? raw.body
        ?? '';

    const mediaRaw = raw.media || raw.attachments || raw.files || [];
    const media = Array.isArray(mediaRaw)
        ? mediaRaw.map((m: any, i: number) => {
            if (typeof m === 'string') return {id: String(i), url: m};
            return {
                id: String(m.id ?? i),
                url: m.url || m.path || m.src || m.mediaUrl || null,
            };
        }).filter((m: any) => m.url)
        : [];

    return {
        ...raw,
        id: Number(raw.id),
        message: raw.message ?? text,
        original_message: text,
        posted_by: postedBy,
        seen: Boolean(raw.seen ?? raw.isSeen ?? raw.read),
        media: media.length ? media : null,
        created_at: asIso(raw.created_at || raw.createdAt) || String(raw.created_at || raw.createdAt || ''),
        updated_at: asIso(raw.updated_at || raw.updatedAt),
    };
}

/**
 * Map app-API chat thread → legacy ChatInterface used by Flirt list/detail.
 */
export function normalizeChat(raw: any, me: number | null = cachedAuthUserId): any {
    if (!raw || typeof raw !== 'object') return raw;

    const participantsRaw = raw.participants || raw.users || raw.members || raw.chatParticipants || [];
    const participants = Array.isArray(participantsRaw)
        ? participantsRaw.map((p: any) => normalizeParticipant(p, me))
        : [];

    const latestRaw = raw.latest_message
        || raw.latestMessage
        || raw.lastMessage
        || raw.last_message
        || raw.recentMessage
        || null;

    const image = raw.image
        || raw.photo
        || raw.photoUrl
        || raw.photo_url
        || raw.avatar
        || null;

    return {
        ...raw,
        id: Number(raw.id),
        name: raw.name || raw.title || raw.subject || 'Chat',
        image,
        participants,
        unseen_count: Number(
            raw.unseen_count
            ?? raw.unseenCount
            ?? raw.unreadCount
            ?? raw.unread
            ?? raw.unseen
            ?? 0,
        ),
        latest_message: latestRaw ? normalizeMessage(latestRaw, me) : null,
        updated_at: asIso(raw.updated_at || raw.updatedAt || raw.latest_message?.created_at)
            || String(raw.updated_at || raw.updatedAt || ''),
        type: raw.type || (participants.length <= 2 ? 'private' : 'group'),
    };
}

export async function normalizeChats(list: any): Promise<any[]> {
    const me = await authUserId();
    if (!Array.isArray(list)) {
        if (list && typeof list === 'object') {
            if (Array.isArray(list.data)) return normalizeChats(list.data);
            if (Array.isArray(list.chats)) return normalizeChats(list.chats);
            if (Array.isArray(list.items)) return normalizeChats(list.items);
        }
        return [];
    }
    return list.map((c) => normalizeChat(c, me)).filter((c) => c && c.id);
}

export async function normalizeChatMessages(list: any): Promise<any[]> {
    const me = await authUserId();
    if (!Array.isArray(list)) {
        if (list && typeof list === 'object' && Array.isArray(list.data)) {
            return normalizeChatMessages(list.data);
        }
        return [];
    }
    return list.map((m) => normalizeMessage(m, me)).filter((m) => m && m.id);
}

export function clearCachedAuthUserId(): void {
    cachedAuthUserId = null;
}
